import React from 'react';

import type { UUID } from '@aline/common';
import { DialogActions, DialogContent } from '@mui/material';
import { AlineButton, AlineDialog } from 'src/components';
import { useDispatchSuccessSnack } from 'src/helpers';
import { useCopyDocument, useGetDocumentsSimple } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

interface DuplicateDocumentDialogProps {
  documentId: UUID;
  handleClose: () => void;
  open: boolean;
  reportId?: UUID;
}

const useStyles = makeStyles()(theme => ({
  container: {
    minWidth: theme.dialogStyles.smallDialogWidth,
  },
  copyButton: {
    marginLeft: theme.spacing(1),
  },
}));

export function DuplicateDocumentDialog({
  documentId,
  handleClose,
  open,
  reportId,
}: DuplicateDocumentDialogProps): JSX.Element {
  const { classes } = useStyles();

  const { documents } = useGetDocumentsSimple([documentId]);
  const document = documents[0];

  const { copyDocument, loading } = useCopyDocument();
  const dispatchSuccessSnack = useDispatchSuccessSnack({
    message: 'Successfully duplicated document!',
  });

  async function handleDuplicateDocument(): Promise<void> {
    if (!documentId) return;

    const { data } = await copyDocument({
      reportId,
      variables: {
        documentId,
        documentProps: {
          name: document?.name ? `${document.name} (Copy)` : undefined,
        },
      },
    });
    if (data) dispatchSuccessSnack();

    handleClose();
  }

  return (
    <AlineDialog
      classes={{ paper: classes.container }}
      open={open}
      title="Duplicate document"
      onClose={handleClose}
    >
      <DialogContent>
        Are you sure you want to duplicate this document? A copy will be created with the same
        content.
      </DialogContent>
      <DialogActions>
        <AlineButton color="primary" variant="outlined" onClick={handleClose}>
          Cancel
        </AlineButton>
        <div className={classes.copyButton}>
          <AlineButton
            color="primary"
            loading={loading}
            type="submit"
            variant="contained"
            onClick={handleDuplicateDocument}
          >
            Duplicate
          </AlineButton>
        </div>
      </DialogActions>
    </AlineDialog>
  );
}
